/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, Tag, Typography, Typography2 } from '@coursera/cds-core';

import SFCBreadcrumb from 'bundles/coursera-plus-common/components/search/SFCBreadcrumb';
import SFCPlayIconSvg from 'bundles/coursera-plus-common/components/search/SFCPlayIconSvg';
import SFCProductInfo from 'bundles/coursera-plus-common/components/search/SFCProductInfo';
import type { Hit } from 'bundles/internal-search/types';
import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import ProductTypePill from 'bundles/search-common/components/ProductTypePill';
import { isSearchTheme } from 'bundles/search-common/searchTheme';
// save/unsave experiment
import { getPartners } from 'bundles/search-common/utils/SearchUtils';

import _t from 'i18n!nls/search-common';

type Props = {
  hit: Hit;
  hitPosition?: number;
};

type SFCDesktopSearchCardImageProps = {
  hit: Hit;
  hitPosition?: number;
};

const SFCDesktopSearchCardImage = ({ hit, hitPosition }: SFCDesktopSearchCardImageProps) => {
  let altTagContent = hit.name;
  let ImgixComponent: typeof Imgix | typeof LazyImgix = Imgix;

  // If card is greater than 5th element in list, use LazyImgix for perf boost.
  if (hitPosition && hitPosition > 4) {
    ImgixComponent = LazyImgix;
  }

  if ((hit.partners || []).length) {
    altTagContent += ` by ${hit.partners.join(', ')}`;
  }

  return (
    <div className="sfc-image">
      <div className="image-wrapper">
        <ImgixComponent
          src={hit.imageUrl || ''}
          alt={altTagContent}
          maxWidth={150}
          maxHeight={84}
          width={150}
          height={84}
          imgParams={{ fit: 'fill', auto: 'format,compress', bg: 'FFF', quality: '25' }}
          className="product-photo"
        />
        <div className="watch-now-banner">
          <Grid container spacing={8}>
            <Grid item>
              <SFCPlayIconSvg className="watch-now-icon" width={20} height={20} />
            </Grid>
            <Grid item alignItems="center" className="watch-now-text">
              <Typography2 css={{ color: 'var(--cds-color-white-0)', fontWeight: '600' }} component="span">
                Watch Now
              </Typography2>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

const SFCDesktopSearchCardFragment = ({ hit, hitPosition }: Props) => {
  const theme = useTheme();
  if (!isSearchTheme(theme)) {
    throw new Error(
      'SFCDesktopSearchCardFragment can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }
  if (!hit) return null;

  const hitID = hit.objectID && hit.objectID.split('~')[1];
  const {
    entityType,
    partners,
    parentCourseId,
    parentCourseName,
    parentLessonName,
    parentLessonObjectUrl,
    videosInLesson,
    durationMs,
    completions,
    objectID,
    cobrandingEnabled,
  } = hit;

  return (
    <Grid container className="card-content" css={{ marginLeft: '6px' }}>
      <Grid item className="card-image">
        <SFCDesktopSearchCardImage hit={hit} hitPosition={hitPosition} />
      </Grid>
      <Grid item className="card-info">
        <Typography component="h2" variant="h2semibold" className="card-title">
          {hit.name}
        </Typography>
        <Grid container>
          <Grid item>
            <Typography2 component="span" variant="bodySecondary" color="supportText" className="partner-name">
              {getPartners(hitID, !!cobrandingEnabled, partners)}
            </Typography2>
          </Grid>
        </Grid>
        <SFCBreadcrumb
          parentCourseId={parentCourseId || ''}
          parentCourseName={parentCourseName || ''}
          parentLessonName={parentLessonName}
          parentLessonObjectUrl={parentLessonObjectUrl}
          entityType={entityType}
          objectID={objectID}
        />
        <Grid container className="product-type-container">
          <Grid item className="product-type-row">
            <ProductTypePill productTypeName={entityType} />
            <Tag
              variant="status"
              priority="secondary"
              color="highlight1"
              css={{ marginLeft: '8px', padding: '2px 8px' }}
            >
              {_t('Beta')}
            </Tag>
          </Grid>
        </Grid>
        <SFCProductInfo videosInLesson={videosInLesson} durationMs={durationMs} completions={completions || 0} />
      </Grid>
    </Grid>
  );
};

export default SFCDesktopSearchCardFragment;
